import { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import CartProductCard from '@/components/cartProductCard';
import { navigateToComboDetail } from '@/talons/combos/comboDetailNavigation';
import { buildComboInstanceDomId } from '@/talons/combos/comboCartAllocationResult';
import * as S from './styles';

interface CartComboGroupItem {
    uid: string;
    displayKey?: string;
    comboInstanceId?: string;
    kitIndex?: number;
    [key: string]: unknown;
}

interface CartComboGroupProps {
    idCampanha: string;
    comboInstanceId: string;
    kitIndex: number;
    kitCount?: number;
    nomeCampanha: string;
    isInstanceComplete: boolean;
    appliesDiscount: boolean;
    hasLooseComboProducts: boolean;
    groupsProgressLabel?: string;
    items: CartComboGroupItem[];
    addProductToCart: (params: {
        sku: number;
        quantity: number;
        option_uid: string;
        uid: string;
        category_id: number;
    }) => void;
    updateProductAmount: (params: { uid: string; sku: number; quantity: number }) => void;
}

const CartComboGroup = ({
    idCampanha,
    nomeCampanha,
    kitIndex,
    kitCount = 1,
    isInstanceComplete,
    appliesDiscount,
    groupsProgressLabel,
    comboInstanceId,
    items,
    addProductToCart,
    updateProductAmount,
}: CartComboGroupProps) => {
    const navigate = useNavigate();

    if (!items.length) return null;

    const showKitLabel = !isInstanceComplete && kitIndex > 1;
    const isInProgress = !isInstanceComplete;
    const isMergedCompleteGroup = isInstanceComplete && kitCount > 1;

    const handleHeaderClick = () => {
        if (!isInProgress) return;
        navigateToComboDetail(navigate, idCampanha);
    };

    const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isInProgress) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleHeaderClick();
        }
    };

    return (
        <S.GroupContainer
            id={buildComboInstanceDomId(comboInstanceId)}
            $isActive={isInstanceComplete}
            aria-label={`Combo ${nomeCampanha}${isMergedCompleteGroup ? ` — ${kitCount}x` : showKitLabel ? ` — Kit ${kitIndex}` : ''}`}
        >
            <S.GroupHeader
                $isActive={isInstanceComplete}
                $clickable={isInProgress}
                role={isInProgress ? 'button' : undefined}
                tabIndex={isInProgress ? 0 : undefined}
                aria-label={
                    isInProgress
                        ? `Ver detalhes do combo ${nomeCampanha}${showKitLabel ? ` — Kit ${kitIndex}` : ''}`
                        : undefined
                }
                onClick={handleHeaderClick}
                onKeyDown={handleHeaderKeyDown}
            >
                <S.GroupHeaderContent>
                    <S.GroupTitle $isActive={isInstanceComplete}>
                        {nomeCampanha}
                        {showKitLabel ? ` — Kit ${kitIndex}` : ''}
                    </S.GroupTitle>

                    {groupsProgressLabel ? (
                        <S.GroupsProgressLabel>{groupsProgressLabel}</S.GroupsProgressLabel>
                    ) : null}

                    {isInProgress ? (
                        <S.NoDiscountNote>Combo em andamento não tem desconto!</S.NoDiscountNote>
                    ) : null}
                </S.GroupHeaderContent>

                <S.GroupHeaderMeta>
                    {!isInstanceComplete && <S.InProgressLabel>Em andamento</S.InProgressLabel>}
                    {isInstanceComplete && appliesDiscount && kitCount > 1 && (
                        <S.KitMultiplier>{kitCount}x</S.KitMultiplier>
                    )}
                    {isInstanceComplete && appliesDiscount && kitCount <= 1 && (
                        <S.KitMultiplier>Completo</S.KitMultiplier>
                    )}
                    {isInstanceComplete && !appliesDiscount && (
                        <S.InProgressLabel>Completo (sem desconto)</S.InProgressLabel>
                    )}
                </S.GroupHeaderMeta>
            </S.GroupHeader>

            <S.GroupProducts>
                {items.map((item, index) => (
                    <S.GroupProductItem key={`combo-group-item-${item.displayKey || item.uid}`} $hasDivider={index < items.length - 1}>
                        <CartProductCard
                            item={item}
                            addProductToCart={addProductToCart}
                            updateProductAmount={updateProductAmount}
                            isInComboGroup
                        />
                    </S.GroupProductItem>
                ))}
            </S.GroupProducts>
        </S.GroupContainer>
    );
};

export default CartComboGroup;
